import { Component } from 'preact'
import ButtonControl from '../../../preact-components/button-control'
import Icon from '../../../preact-components/assets'
import SearchBox from '../../../preact-components/search-box/SearchBox'
import PillContainer from '../../../preact-components/pills/PillContainer'
import Pill from '../../../preact-components/pills/Pill'
import { Filters } from '..'
import FiltersMenu, { FilterList, FilterSelector } from './FiltersMenu'

export interface OverviewFiltersProps {
  filters: FilterList
  appliedFilters?: Filters
  onFilterChange: (filter: string, payload: any) => Promise<void> | void
  onClearFilterCriteria: () => Promise<void> | void
  withSearch?: boolean
  searchPlaceholder?: string
  areAppliedFiltersHiddenByDefault?: boolean
  label?: string
  hideBtnLabel?: string
  clearFiltersLabel?: string
}

interface State {
  areFiltersOpen: boolean
  areFiltersOpenFromFilterPill: boolean
  areFiltersOpenFromMain: boolean
  areAppliedFiltersVisible: boolean
  selectedFilter: string | undefined
  filters: FilterSelector[]
}

class OverviewFilters extends Component<OverviewFiltersProps, State> {
  wrapperRef: null | HTMLElement = null
  constructor (props: OverviewFiltersProps) {
    super(props)

    this.state = {
      areFiltersOpen: false,
      areFiltersOpenFromFilterPill: false,
      areFiltersOpenFromMain: false,
      areAppliedFiltersVisible: !props.areAppliedFiltersHiddenByDefault,
      selectedFilter: undefined,
      filters: props.filters.filter(filter => filter.type === 'filter') as FilterSelector[]
    }
  }

  addHandler () {
    const { areFiltersOpen, areFiltersOpenFromFilterPill, areFiltersOpenFromMain } = this.state
    if (areFiltersOpen || areFiltersOpenFromFilterPill || areFiltersOpenFromMain) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.setState({
        areFiltersOpenFromFilterPill: false,
        areFiltersOpenFromMain: false,
        areFiltersOpen: false
      }, this.removeHandler)
    }
  }

  handleDropdownToggle = () => {
    this.setState(({ areFiltersOpen, areFiltersOpenFromFilterPill, areFiltersOpenFromMain }) => ({
      areFiltersOpen: !areFiltersOpen,
      areFiltersOpenFromFilterPill: (areFiltersOpen && areFiltersOpenFromFilterPill),
      areFiltersOpenFromMain: (areFiltersOpen && areFiltersOpenFromMain),
      selectedFilter: undefined
    }), this.addHandler)
  }

  toggleAppliedFiltersVisibility = () => {
    this.setState(({ areAppliedFiltersVisible }) => ({ areAppliedFiltersVisible: !areAppliedFiltersVisible }))
  }

  onLoad = (filterId: string, items: any[]) => {
    this.setState(({ filters }) => {
      const filterIndex = filters.findIndex(filter => filter.id === filterId)
      const updatedFilter = { ...filters[filterIndex], items }

      return {
        filters: [
          ...filters.slice(0, filterIndex),
          updatedFilter,
          ...filters.slice(filterIndex + 1)
        ]
      }
    })
  }

  getDisplayAppliedFilters () {
    const { filters } = this.state

    const { appliedFilters } = this.props

    if (!appliedFilters) return []

    return Object.keys(appliedFilters)
      .filter((key: string) => {
        // ignore search
        if (key === 'search') {
          return false
        }

        const value = appliedFilters[key]
        // ignore empty arrays
        if (Array.isArray(value) && !value.length) {
          return false
        }

        return (value === false) || !!value
      })
      .map((key: string) => {
        const values = appliedFilters[key]
        const filter = filters.find(filter => filter.id === key) as FilterSelector

        return {
          filter: key,
          name: filter.name,
          pillValue: filter.getPillDisplayValue(values)
        }
      })
  }

  onApplyFilters = (id: string, data: any) => {
    this.props.onFilterChange(id, data)
    this.onCancelFilters()
  }

  onCancelFilters = () => {
    this.setState({
      areFiltersOpen: false,
      areFiltersOpenFromFilterPill: false,
      areFiltersOpenFromMain: false,
      selectedFilter: undefined
    })
  }

  onClearFilterCriteria = () => {
    this.props.onClearFilterCriteria()
  }

  getFiltersMenu () {
    const { areFiltersOpenFromFilterPill, selectedFilter } = this.state
    const { appliedFilters, filters } = this.props

    const menuProps = {
      areFiltersOpenFromFilterPill,
      selectedFilter,
      appliedFilters,
      filters,
      onFilterClick: (filterId: string) => this.setState({ selectedFilter: filterId }),
      onFilterBack: () => this.setState({ selectedFilter: undefined }),
      onApplyFilters: this.onApplyFilters,
      onCancelFilters: this.onCancelFilters,
      onLoad: this.onLoad
    }

    return <FiltersMenu {...menuProps} />
  }

  render() {
    const { filters, appliedFilters, onFilterChange, withSearch, searchPlaceholder, label = 'Filters', hideBtnLabel = 'Hide filters', clearFiltersLabel = 'Clear all' } = this.props
    const { areAppliedFiltersVisible, selectedFilter, areFiltersOpen, areFiltersOpenFromFilterPill, areFiltersOpenFromMain } = this.state

    const displayAppliedFilters = this.getDisplayAppliedFilters()
    const areFiltersApplied = displayAppliedFilters.length > 0
    const mainFilters = filters.filter(filter => filter.type === 'filter' && filter.isMain) as FilterSelector[]

    return (
      <div ref={node => { this.wrapperRef = node }}>
        <div className='overview__filters-container'>
          {mainFilters.map(mainFilter => <div key={mainFilter.id}>
            <ButtonControl
              key={mainFilter.id}
              newStyle
              title={mainFilter.name}
              size='medium'
              class='overview__filters-container-open-button qa-overview-open-filters' // eslint-disable-line
              onClick={() => this.setState({
                areFiltersOpen: false,
                areFiltersOpenFromFilterPill: false,
                areFiltersOpenFromMain: !areFiltersOpenFromMain,
                selectedFilter: mainFilter.id
              }, this.addHandler)}
            />
            {areFiltersOpenFromMain && selectedFilter === mainFilter.id &&
              <div className='overview__filters-box-container'>
                <div className='overview__filters-box'>{this.getFiltersMenu()}</div>
              </div>}
          </div>)}
          <div>
            <ButtonControl
              newStyle
              title={<div>
                <Icon icon='filter' height={12} width={12} />
                {label}
              </div>}
              size='medium'
              class='overview__filters-container-open-button qa-overview-open-filters' // eslint-disable-line
              onClick={this.handleDropdownToggle}
            />
            {areFiltersOpen && <div className='overview__filters-box-container'>
              <div className='overview__filters-box'>{this.getFiltersMenu()}</div>
            </div>}
          </div>
          {withSearch && (
            <SearchBox
              placeholder={searchPlaceholder}
              value={appliedFilters?.search}
              onSubmit={(value: string) => onFilterChange('search', value)}
              onClear={() => onFilterChange('search', '')}
              isModerationOverview
            />
          )}
          {!areAppliedFiltersVisible && !!displayAppliedFilters?.length && (
            <ButtonControl
              newStyle
              outlined
              size='medium'
              title={`${displayAppliedFilters?.length} Hidden filter${displayAppliedFilters.length > 1 ? 's' : ''}`}
              variant='brand'
              class='qa-overview-hide-filters' // eslint-disable-line
              onClick={this.toggleAppliedFiltersVisibility}
            />
          )}
        </div>
        {areAppliedFiltersVisible && areFiltersApplied && (
          <PillContainer>
            {displayAppliedFilters.map(({ filter, name, pillValue }) => {
              return (
                <div key={filter}>
                  <Pill newStyle>
                    <div
                      className='truncated' onClick={() => this.setState({
                        areFiltersOpen: false,
                        areFiltersOpenFromFilterPill: !areFiltersOpenFromFilterPill,
                        areFiltersOpenFromMain: false,
                        selectedFilter: filter
                      }, this.addHandler)}
                    >
                      <span className='filter-type'>{name}</span>
                      <span className='filter-value'>
                        {pillValue}
                      </span>
                    </div>
                    <Icon
                      customClass={`filter-pill-dismiss qa-dismiss-${filter}-filter-pill`} icon='close' width={16}
                      height={16}
                      onClick={() => onFilterChange(filter, null)}
                    />
                  </Pill>
                  {areFiltersOpenFromFilterPill && selectedFilter === filter && <div className='overview__filters-box-container'>
                    <div className='overview__filters-box'>{this.getFiltersMenu()}</div>
                  </div>}
                </div>
              )
            }
            )}
            <ButtonControl
              newStyle
              outlined
              size='medium'
              class='qa-overview-clear-filters' // eslint-disable-line
              title={clearFiltersLabel}
              variant='danger'
              onClick={this.onClearFilterCriteria}
            />
            <ButtonControl
              newStyle
              outlined
              size='medium'
              class='qa-overview-hide-filters' // eslint-disable-line
              title={hideBtnLabel}
              variant='secondary-reversed'
              onClick={this.toggleAppliedFiltersVisibility}
            />
          </PillContainer>
        )}
      </div>
    )
  }
}

export default OverviewFilters
